import { build, pool, type BuildTask } from 'src/app/common/classes/request-builder';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';
import type { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import type { ParcelasFormDefinition } from './form-definition';

const DECLARATIVE_DROPDOWN_FORM_TYPE_LIMIT = 3;

export interface ParcelasFormDataLoaderEndpoints {
    readonly portainjertosGET: RequestHandler<any>;
    readonly sistemasRiegoGET: RequestHandler<any>;
    readonly comunidadesRegantesGET: RequestHandler<any>;
    readonly origenesAguaRiegoGET: RequestHandler<any>;
    readonly fincasGET: RequestHandler<any>;
    readonly sectoresGET: RequestHandler<any>;
    readonly compradoresGET: RequestHandler<any>;
    readonly usuariosGET: RequestHandler<any>;
    readonly trabajadoresGET: RequestHandler<any>;
    readonly cultivosGET: RequestHandler<any>;
    readonly variedadesGET: RequestHandler<any>;
}

export interface ParcelasFormDataLoaderContext {
    readonly form: ParcelasFormDefinition;
    readonly formType: number;
    readonly env: ParcelasEnvironmentDefinition;
    readonly endpoints: ParcelasFormDataLoaderEndpoints;
    readonly addDefaultSequiadorAplicador: (row: MantenimientoModel) => void;
    readonly setLoadingData: (loading: boolean) => void;
}

export class ParcelasFormDataLoader {
    public static load(context: ParcelasFormDataLoaderContext): Promise<boolean> {
        context.setLoadingData(true);

        return pool(ParcelasFormDataLoader.createBuildTasks(context))
            .then(() => true)
            .finally(() => {
                context.setLoadingData(false);
            });
    }

    public static createBuildTasks(context: ParcelasFormDataLoaderContext): BuildTask[] {
        const builds: BuildTask[] = [
            build(context.endpoints.portainjertosGET, context.form.lists.patronVina, ['sinonimo']),
        ];

        if (!ParcelasFormDataLoader.usesDeclarativeSimpleDropdowns(context.formType)) {
            builds.push(
                build(context.endpoints.sistemasRiegoGET, context.form.lists.sistemasRiego, ['sistema_de_riego'], true),
                build(context.endpoints.comunidadesRegantesGET, context.form.lists.comunidadesRegantes, 'nombre'),
                build(context.endpoints.origenesAguaRiegoGET, context.form.lists.origenesAguaRiego, 'procedencia_del_agua_de_riego'),
            );
        }

        if (!ParcelasFormDataLoader.usesDeclarativeFincaSectorDropdowns(context.formType)) {
            builds.push(
                build(context.endpoints.fincasGET, context.form.lists.fincas, ['nombre']),
                build(context.endpoints.sectoresGET, context.form.lists.sectores, ['nombre'], true),
            );
        }

        if (context.env.showParcelasPartida) {
            builds.push(build(context.endpoints.compradoresGET, context.form.lists.comercios, 'razon_social'));
        }

        if (!ParcelasFormDataLoader.usesDeclarativeSimpleDropdowns(context.formType)) {
            builds.push(ParcelasFormDataLoader.createAplicadoresBuildTask(context));
        }

        if (context.env.appName === 'avafitogest') {
            builds.push(build(
                context.endpoints.usuariosGET,
                context.form.lists.usuarios,
                (it: MantenimientoModel) => it.nombre + ' ' + (it.apellidos || ''),
                true,
            ));
        }

        if (context.env.hasPlantas || !context.env.activateApiSIEX) {
            builds.push(
                build(context.endpoints.cultivosGET, context.form.lists.cultivosLegacy, ['cultivo'], false, false),
                build(context.endpoints.variedadesGET, context.form.lists.variedadesLegacy, ['variedad']),
            );
        }

        return builds;
    }

    public static usesDeclarativeFincaSectorDropdowns(formType: number): boolean {
        return formType < DECLARATIVE_DROPDOWN_FORM_TYPE_LIMIT;
    }

    public static usesDeclarativeSimpleDropdowns(formType: number): boolean {
        return formType < DECLARATIVE_DROPDOWN_FORM_TYPE_LIMIT;
    }

    private static createAplicadoresBuildTask(context: ParcelasFormDataLoaderContext): BuildTask {
        if (context.env.appName === 'sequiador') {
            return build(
                context.endpoints.usuariosGET,
                context.form.lists.aplicadores,
                (it: MantenimientoModel) => {
                    context.addDefaultSequiadorAplicador(it);

                    return it.nombre + ' ' + (it.apellidos || '');
                },
                true,
            );
        }

        return build(
            context.endpoints.trabajadoresGET,
            context.form.lists.aplicadores,
            (it: MantenimientoModel) => it.nombre + ' ' + (it.apellidos || ''),
            true,
        );
    }
}
